import CommentIcon from "@mui/icons-material/Comment";
import DeleteIcon from "@mui/icons-material/Delete";
import GradingIcon from '@mui/icons-material/Grading';
import GroupsIcon from '@mui/icons-material/Groups';
import IosShareIcon from '@mui/icons-material/IosShare';
import SendIcon from "@mui/icons-material/Send";
import Badge from "@mui/material/Badge";
import IconButton from "@mui/material/IconButton";
import Portal from "@mui/material/Portal";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { keyframes } from "@mui/material";
import { styled } from "@mui/material/styles";
import { ConfirmResponse, useConfirmDialog, } from "app/providers/confirm-dialog";
import { useDialog } from "app/providers/dialog";
import { useAttorneyHubDispatch } from "app/realtime-store/redux-store";
import { CanceledError } from "axios";
import Loader from "common/components/loader";
import LoadingButton from "common/components/loading-button";
import { AccountType } from "common/values/account-type/account-type";
import Guid from "common/values/guid/guid";
import _ from "lodash";
import Individual from "marketplace/entities/individual/individual";
import MarketplaceTeamAPIService from "marketplace/entities/marketplace-team/api/marketplace-team-api-service";
import IndividualProfile from "marketplace/values/individual-profile/individual-profile";
import { enqueueSnackbar } from "notistack";
import React, { useEffect } from "react";
import { useSession } from "users/session/session-context";
import { getCommentThreadsByProposal } from "work/entities/comment-thread/store/comment-thread-redux-slice";
import AutoCommentGenerator from "work/entities/comment/auto-comment-generator";
import {
  getArePendingComments,
  publishComments,
  updateAutoGeneratedComments,
} from "work/entities/comment/store/comments-redux-slice";
import CommentsDrawer from "work/entities/comment/view/comments-drawer";
import EntityClientRepresentative from "work/entities/entity-client-representative/entity-client-representative";
import ProposalAPIService from "work/entities/proposal/api/proposal-api-service";
import ConflictsTab from "work/entities/proposal/draft/view/tabs/conflicts-tab";
import DetailsTab from "work/entities/proposal/draft/view/tabs/details-tab";
import DiscountTab from "work/entities/proposal/draft/view/tabs/discount-tab";
import FeeScheduleTab from "work/entities/proposal/draft/view/tabs/fee-schedule-tab";
import PoliciesTab from "work/entities/proposal/draft/view/tabs/policies-tab";
import TeamTab from "work/entities/proposal/draft/view/tabs/team-tab";
import Proposal, { ProposalField, ProposalFieldCategory, } from "work/entities/proposal/proposal";
import {
  addProposal,
  addProposalBuilder,
  getProposalBuilderByProposalId,
  getProposalById,
  removeProposal,
  replaceProposalBuilder,
} from "work/entities/proposal/store/proposals-redux-slice";
import ProposalBuilder from "work/entities/proposal/utils/proposal-builder";
import IssueTraverser from "work/entities/proposal/view/issue-traverser";
import WorkAgreementAPIService from "work/entities/work-agreement/api/work-agreement-api-service";
import WorkAgreement, { Masked } from "work/entities/work-agreement/work-agreement";
import { ProposalFieldName } from "work/values/constants";
import ProjectName from "work/values/project-name/project-name";
import ProposalIssues from "work/values/proposal-issues/proposal-issues";
import ProposalIssuesBadge from "work/values/proposal-issues/view/proposal-issues-badge";
import ProposalReviewer from "work/values/proposal-reviewer";
import SelectedRepresentative from "work/values/selected-representative/selected-representative";
import SelectedVendorTeam from "work/values/selected-vendor-team/selected-vendor-team";
import DetailedVendorTeam from "work/values/vendor-team/detailed-vendor-team";
import ReviewerSelector from "work/view/components/reviewer-selector";

const pulse = keyframes`
  0% {
    transform: scale(0.9);
    opacity: 0.85;
  }
  50% {
    transform: scale(1.2);
    opacity: 1.0;
  }
  100% {
    transform: scale(0.9);
    opacity: 0.85;
  }
`;
const Header = styled("section")(
  ({ theme }) => ({
    backgroundColor: theme.palette.background.default,
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    paddingBottom: theme.spacing(1),
    position: "sticky",
    top: "0px",
    zIndex: 10,
  }));
const TabsContainer = styled(Tabs)(
  ({ theme }) => ({
    "&.MuiTabs-root": {
      overflow: "hidden",
      "& .MuiTabs-scroller": {
        paddingTop: theme.spacing(0.5),
      },
    },
  }));
const Content = styled("section")(
  () => ({
    alignItems: 'center',
    display: "flex",
    flex: 1,
    flexDirection: "row",
    height: '25rem',
    width: '72rem'
  }));
const TabContent = styled("section")(
  ({ theme }) => ({
    alignItems: "stretch",
    flexDirection: "column",
    display: "flex",
    flex: 1,
    margin: theme.spacing(
      2,
      0
    ),
    overflow: "visible",
    width: "100%",
  }));
const ActionsContainer = styled("section")(
  ({ theme }) => ({
    [theme.breakpoints.down("md")]: {
      paddingBottom: theme.spacing(1),
      paddingTop: theme.spacing(1),
    },
    backgroundColor: theme.palette.background.default,
    bottom: "0px",
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    paddingBottom: theme.spacing(2.5),
    position: "sticky",
    width: "100%",
    zIndex: 10,
  }));
const SecondaryActions = styled("section")(
  ({ theme }) => ({
    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
    },
    alignItems: "center",
    display: "flex",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
    width: "100%",
  }));
const ProposalActions = styled("section")(
  ({ theme }) => ({
    [theme.breakpoints.down("md")]: {
      display: "flex",
      flexDirection: "column",
      flexGrow: 1,
      flexWrap: "nowrap",
    },
    alignContent: "end",
    alignItems: "center",
    display: "grid",
    flex: 1,
    gridTemplateColumns: "repeat(auto-fit, minmax(15rem, 1fr))",
    gap: theme.spacing(1),
    minHeight: "64px",
  }));
const ProposalActionSpan = styled("span")(
  ({ theme }) => ({
    minWidth: theme.spacing(24),
    width: "100%"
  }));
const ProposalActionButton = styled(LoadingButton)(
  () => ({
    width: "100%",
    whiteSpace: "nowrap",
    "&.Mui-disabled": {
      color: "rgba(0, 0, 0, 0.26) !important",
      backgroundColor: "rgba(0, 0, 0, 0.12) !important",
    },
  }));
const ReviewerBadge = styled(Badge)(
  () => ({
    marginLeft: '1rem'
  }));
const DialogTab = styled(Tab)(
  () => ({
    "&.MuiTab-root": {
      overflow: "visible",
    },
  }));
const CommentButtons = styled("section")(
  ({ theme }) => ({
    alignContent: "center",
    marginLeft: theme.spacing(2),
  }));
const ButtonContainer = styled("div")(
  () => ({
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
  }));
const HighlightCommentButton = styled(
  IconButton,
  { shouldForwardProp: (prop) => prop !== 'active' }
)<{ active?: boolean }>(({ active }) => ({
  paddingBottom: 0,
  position: 'relative',
  zIndex: 0,
  ...(active && {
    animation: `${pulse} 1.5s ease-in-out infinite`,
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(21, 101, 192, 0.05)',
      borderRadius: '50%',
      animation: `${pulse} 1.5s ease-in-out infinite`,
      zIndex: -1,
    },
  }),
}));
const CommentButtonLabel = styled(Typography)(
  () => ({
    fontSize: "0.6em",
  }));
const NoBuilderLoader = styled(Loader)(
  ({ theme }) => ({
    alignItems: "center",
    display: "flex",
    justifyContent: "center",
    height: "100%",
    padding: theme.spacing(2),
    width: "100%",
  }));
const Fieldset = styled('span')(
  ({ theme }) => ({
    border: "1px solid",
    borderColor: theme.palette.divider,
    borderRadius: theme.shape.borderRadius,
    display: "grid",
    gap: theme.spacing(1),
    gridColumnEnd: "span 2",
    gridTemplateColumns: "repeat(auto-fit, minmax(15rem, 1fr))",
    padding: theme.spacing(1),
    width: "100%",
  }));

type ProposalDraftDialogProps = {
  proposalId?: Guid;
  disableEditing?: boolean;
  preSelectedRep?: SelectedRepresentative;
  preSelectedTeam?: SelectedVendorTeam;
  existingWorkAgreement?: WorkAgreement;
  navigate: (url: string) => void;
  isSaving: boolean;
  onSaveCallback?: (
    builder: ProposalBuilder,
    preventNavigation?: boolean
  ) => Promise<Proposal | undefined>;
  onProposalSubmitted?: (submittedProposal: Proposal) => void;
};

export default function ProposalDraftDialog(
  props: Readonly<ProposalDraftDialogProps>
) {
  const {
    proposalId,
    disableEditing,
    preSelectedTeam,
    preSelectedRep,
    existingWorkAgreement,
    isSaving,
    navigate,
    onSaveCallback,
    onProposalSubmitted,
  } = props;

  const session = useSession();
  const { closeDialog } = useDialog();
  const confirm = useConfirmDialog();

  const [activeTab, setActiveTab] = React.useState<ProposalFieldCategory>(
    ProposalFieldCategory.Details
  );
  const [currentCommentField, setCurrentCommentField] = React.useState<
    ProposalField | undefined
  >();
  const [activeReviewField, setActiveReviewField] = React.useState<ProposalField>();
  const [commentsPanelTitle, setCommentsPanelTitle] = React.useState<
    string | undefined
  >();
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
  const [reviewerSelectorAnchor, setReviewerSelectorAnchor] =
    React.useState<HTMLButtonElement>();
  const [isManagingReviewers, setIsManagingReviewers] =
    React.useState<boolean>(false);
  const [isApprovingReview, setIsApprovingReview] =
    React.useState<boolean>(false);
  const [isCommentsPanelOpen, setIsCommentsPanelOpen] = React.useState<boolean>(false);
  const [isSelectingClient, setIsSelectingClient] =
    React.useState<boolean>(false);
  const [keepCommentsPanelClosed, setKeepCommentsPanelClosed] =
    React.useState<boolean>(false);

  const dispatch = useAttorneyHubDispatch();

  let proposal = getProposalById(proposalId);
  const proposalBuilder = getProposalBuilderByProposalId(
    proposalId ?? Guid.empty
  );
  const issues = ProposalIssues?.fromBuilder(proposalBuilder);
  const commentThreads = getCommentThreadsByProposal(proposal);
  const arePendingComments = getArePendingComments(proposal);
  const isReviewing = [
    ...(proposal?.clientReviewers ?? []),
    ...(proposal?.vendorReviewers ?? [])
  ].some((reviewer) => reviewer.userId.isEqualTo(session.user?.id)) ?? false;
  const vendorReviewerCount =
    proposalBuilder?.currentSpec.vendorReviewers?.length ||
    0;
  const clientReviewerCount =
    proposalBuilder?.currentSpec.clientReviewers?.length ||
    0;
  const hasReviewers = session.context?.viewingAsVendor
    ? vendorReviewerCount > 0
    : clientReviewerCount > 0;
  const generalFieldUnreadCount = commentThreads?.filter((thread) =>
    thread.field.isEqualTo(ProposalField.General)).reduce((acc, thread) =>
      acc + (thread.numUnread ?? 0), 0) ?? 0;

  useEffect(
    () => {
      const abortController = new AbortController();
      if (!proposalBuilder) {
        initProposalBuilder(abortController).then((newProposalBuilder) => {
          dispatch(
            addProposalBuilder({
              proposalId: proposalId ?? Guid.empty,
              builder: newProposalBuilder,
            })
          );
        });
      }
      return () => {
        abortController.abort();
      };
    },
    []
  );

  useEffect(
    () => {
      if (!proposalBuilder) return;
      void generateChangeAutoComments(proposalBuilder);
    },
    [proposalBuilder]
  );

  useEffect(
    () => {
      if (!session?.canSwitchContext || !proposal) return;
      if (
        Masked.isNotMasked(proposal.vendorTeam) &&
        session.context?.viewingAsVendor &&
        proposal.client?.userId.isEqualTo(session.user?.id)
      ) {
        void session.setAccountViewType(AccountType.Client);
      } else if (
        Masked.isNotMasked(proposal.vendorTeam) &&
        (proposal.vendorTeam?.leader?.userId.isEqualTo(session.user?.id) ||
          proposal.vendorTeam?.members?.some((member) =>
            member.userId.isEqualTo(session.user?.id)
          ))
      ) {
        void session.setAccountViewType(AccountType.Vendor);
      }
    },
    [proposal]
  );

  useEffect(
    () => {
      if (!proposal) return;
      if (activeReviewField?.category !== activeTab) {
        setActiveTab(activeReviewField?.category ?? activeTab);
      }
      if (activeReviewField) {
        handleOpenCommentsPanel(activeReviewField);
      }
    },
    [activeReviewField]
  )

  /**
     * Sets the active review field to the first issue/comment in the active tab
     */
  useEffect(
    () => {
      if (activeReviewField?.category === activeTab) return;
      const proposalFieldCategories = Object.values(ProposalFieldCategory);
      const proposalFieldNames = Object.values(ProposalFieldName);
      const tabIssues: ProposalField[] = _.uniqBy(
        issues?.entries.map((issue) => issue.field)
          .filter(field => field.category === activeTab) ?? [],
        (field) => field.toJSON()
      );
      const tabUnreadComments: ProposalField[] = _.uniqBy(
        commentThreads?.filter((thread) => thread.numUnread && thread.field.category === activeTab)
          .map((thread) => thread.field) ?? [],
        (field) => field.toJSON()
      );
      const fieldsPendingReview: ProposalField[] = _.chain([...tabUnreadComments, ...tabIssues])
        .uniqBy((field) => `${field.category}-${field.name}-${field.id?.value ?? ''}`)
        .sortBy([
          (field) => proposalFieldCategories.indexOf(field.category),
          (field) => proposalFieldNames.indexOf(field.name)
        ])
        .value();
      if (fieldsPendingReview[0] && fieldsPendingReview[0].category !== activeTab) {
        setActiveReviewField(undefined);
        return;
      }
      setActiveReviewField(fieldsPendingReview[0]);
    },
    [activeTab]
  )

  async function initProposalBuilder(
    abortController?: AbortController
  ): Promise<ProposalBuilder> {
    if (!session.user?.id) {
      throw new Error("No user to initialize proposal builder");
    }
    let newProposalBuilder = new ProposalBuilder(
      session.user,
      proposal?.spec
    );

    if (existingWorkAgreement) {
      newProposalBuilder = ProposalBuilder.fromWorkAgreement(
        session.user,
        existingWorkAgreement
      );
    } else if (preSelectedRep) {
      // If target rep is a client only
      if (preSelectedRep.isClientRep && !preSelectedRep.isVendorRep) {
        newProposalBuilder = handlePreselectedClient(
          newProposalBuilder,
          preSelectedRep
        );
      } else if (preSelectedRep.isVendorRep) {
        newProposalBuilder = handlePreselectedTeamLeader(
          newProposalBuilder,
          preSelectedRep
        );
      }
    } else if (!newProposalBuilder.currentSpec.client && !session.context?.viewingAsVendor) {
      newProposalBuilder = newProposalBuilder.setClient(
        new EntityClientRepresentative(
          session.user.id,
          session.currentEntity.id,
          session.user.name
        )
      ).cleanStack();
    } else if (preSelectedTeam) {
      try {
        newProposalBuilder = await handlePreselectedTeam(
          newProposalBuilder,
          preSelectedTeam,
          abortController
        );
      } catch (error) {
        if (error instanceof CanceledError) return newProposalBuilder;
        console.error(error);
      }
    }

    return newProposalBuilder;
  }

  function handlePreselectedClient(
    builder: ProposalBuilder,
    client: SelectedRepresentative
  ): ProposalBuilder {
    const clientRep = new EntityClientRepresentative(
      client.userId,
      client.entityId,
      client.name
    );
    return builder.setClient(clientRep);
  }

  function handlePreselectedTeamLeader(
    builder: ProposalBuilder,
    leader: SelectedRepresentative
  ): ProposalBuilder {
    const leaderIndividual = new Individual(
      leader.userId,
      leader.entityId,
      new IndividualProfile(
        leader.userId,
        leader.name.firstName ?? "",
        leader.name.lastName ?? ""
      )
    );
    return builder
      .setName(new ProjectName("New Proposal"))
      .setTeam(new DetailedVendorTeam(
        leaderIndividual,
        []
      ));
  }

  async function handlePreselectedTeam(
    proposalBuilder: ProposalBuilder,
    preSelectedTeam: SelectedVendorTeam,
    abortController?: AbortController
  ): Promise<ProposalBuilder> {
    const marketplaceTeamAPIService = new MarketplaceTeamAPIService(session);
    const team = await marketplaceTeamAPIService.getTeamById(
      preSelectedTeam.targetTeamId,
      abortController
    );

    return proposalBuilder
      .setName(new ProjectName("New Proposal"))
      .setTeam(team.toDetailedTeam());
  }

  function handleTabChange(
    _event: React.ChangeEvent<object> | null,
    newTab: ProposalFieldCategory
  ) {
    setActiveTab(newTab);
  }

  function handleManageReviewersClicked(
    event: React.MouseEvent<HTMLButtonElement>
  ) {
    setReviewerSelectorAnchor(event.currentTarget);
    setIsManagingReviewers(true);
  }

  async function handleSubmitClicked() {
    if (proposal?.isArchived) return;
    if (!proposalBuilder) {
      throw new Error("Proposal cannot be submitted without a builder.");
    }
    try {
      let message: string;
      const clientReviewers = proposalBuilder?.currentSpec.clientReviewers ?? proposal?.clientReviewers ?? [];
      const vendorReviewers = proposalBuilder?.currentSpec.vendorReviewers ?? proposal?.vendorReviewers ?? [];
      if ((
        session.context?.viewingAsVendor &&
        vendorReviewers.length &&
        vendorReviewers.length !== vendorReviewers.filter(reviewer => reviewer.dateApproved).length
      ) || (
          !session.context?.viewingAsVendor &&
          clientReviewers.length &&
          clientReviewers.length !== clientReviewers.filter(reviewer => reviewer.dateApproved).length
        )) {
        message = "Not all reviewers have reviewed the proposal. Do you still want to submit the proposal?";
      } else {
        message = "Submit the proposal now?";
      }

      const response = await confirm({
        title: "Submit Proposal",
        message: message,
        okButtonText: "Submit",
      });

      if (response === ConfirmResponse.Cancel) return;

      setIsSubmitting(true);

      proposal ??= await onSaveCallback?.(proposalBuilder, true);

      const draftProposal = await handleSaveAndShareChanges(false);
      if (!draftProposal) {
        enqueueSnackbar(
          "Failed to submit proposal",
          { variant: "error" }
        );
        return;
      }
      dispatch(publishComments(session));
      const {
        proposal: submittedProposal
      } = await draftProposal.submit(session.user?.id);
      onProposalSubmitted?.(submittedProposal);
      enqueueSnackbar(
        "Proposal submitted successfully",
        { variant: "success" }
      );
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        "Failed to submit proposal",
        { variant: "error" }
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  function shouldDisableTabs(): boolean {
    return isSaving || isManagingReviewers || isSelectingClient;
  }

  async function handleDeleteClicked() {
    if (proposal?.isArchived) return;
    if (!proposal?.id) {
      console.warn("Proposal not found");
      return;
    }

    try {
      const rejectResponse = await confirm({
        title: "Delete Draft Proposal?",
        message: "This cannot be undone.",
      });

      if (rejectResponse === ConfirmResponse.Cancel) return;

      setIsSubmitting(true);
      navigate(`/proposals/drafts/`);
      await proposal.delete(session.user?.id);
      dispatch(removeProposal(proposal.id));
      closeDialog();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        "Failed to cancel proposal",
        { variant: "error" }
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleProposalBuilderUpdated(
    updatedProposalBuilder: ProposalBuilder
  ) {
    if (proposal?.isArchived) return;
    dispatch(
      replaceProposalBuilder({
        proposalId: proposal?.id ?? Guid.empty,
        builder: updatedProposalBuilder,
      })
    );
  }

  async function generateChangeAutoComments(proposalBuilder: ProposalBuilder) {
    if (!proposal?.id || proposal?.isArchived) return;
    const autoComments = new AutoCommentGenerator(
      session,
      proposal,
      proposalBuilder,
      commentThreads ?? []
    ).generateAutoCommentsFromBuilder();
    dispatch(updateAutoGeneratedComments(autoComments));
  }

  function handleCloseCommentsPanel() {
    setKeepCommentsPanelClosed(true);
    setIsCommentsPanelOpen(false);
    adjustDialogPosition(true);
  }

  function handleOpenCommentsPanel(
    field?: ProposalField,
    title?: string
  ) {
    if (field) setCurrentCommentField(field);
    if (title) setCommentsPanelTitle(title);
    setIsCommentsPanelOpen(true);
    adjustDialogPosition(false);
  }

  function adjustDialogPosition(panelOpen: boolean) {
    for (const dialog of document.getElementsByClassName("MuiDialog-root")) {
      dialog.setAttribute(
        "style",
        `padding-right: ${panelOpen ? "0px" : "400px"
        }; transition: padding-right 225ms;`
      );
    }
  }

  async function handleMarkReviewedClicked() {
    if (!proposal) throw new Error("No proposal to mark reviewed");

    try {
      const response = await confirm({
        title: "Mark As Reviewed",
        message: "Are you sure you want to mark this proposal as reviewed?",
        okButtonText: "Mark Reviewed",
      });

      if (response === ConfirmResponse.Cancel) return;

      setIsApprovingReview(true);
      const workAgreementAPIService = new WorkAgreementAPIService(session);
      const proposalService = new ProposalAPIService(session, workAgreementAPIService);
      await proposalService.giveReviewerApproval(proposal);
      enqueueSnackbar(
        "Proposal marked as reviewed",
        { variant: "success" }
      );
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        "Failed to mark proposal as reviewed",
        { variant: "error" }
      );
    } finally {
      setIsApprovingReview(false);
    }
  }

  async function handleSaveAndShareChanges(
    confirmShare: boolean = true
  ): Promise<Proposal | null | undefined> {
    let savedProposal = proposal;
    if (!proposalBuilder || !proposal) {
      throw new Error("No proposal builder to update");
    }
    if (!hasReviewers) {
      throw new Error("Cannot share proposal without any reviewers");
    }

    if (confirmShare) {
      const response = await confirm({
        title: "Share",
        message: "Are you sure you want to share any pending comments and/or changes for review?",
        okButtonText: "Share"
      });

      if (response === ConfirmResponse.Cancel) return;
    }

    if (proposal.userCanEdit(session.user)) {
      const updatedDraft = proposalBuilder.updateDraftProposal(
        proposal,
        session
      );
      const updatedProposal = await updatedDraft.share(session);
      dispatch(addProposal(updatedProposal));
      savedProposal = updatedProposal;
    }

    dispatch(publishComments(session));
    if (confirmShare) {
      enqueueSnackbar(
        "Proposal comments/changes shared with reviewers",
        { variant: "success" }
      );
    }
    return savedProposal;
  }

  function hasUserApprovedReview(): boolean {
    if (!proposalBuilder) {
      throw new Error("No proposal or proposal builder to check");
    }

    const reviewers: ProposalReviewer[] = [
      ...(proposalBuilder.currentSpec.clientReviewers ?? []),
      ...(proposal?.clientReviewers ?? []),
      ...(proposalBuilder.currentSpec.vendorReviewers ?? []),
      ...(proposal?.vendorReviewers ?? [])
    ];

    return reviewers.some(
      (reviewer) =>
        reviewer.userId.isEqualTo(session.user?.id) && reviewer.dateApproved
    );
  }

  function getShareButtonLabel() {
    if (isSubmitting) return "Submitting...";
    if (isSaving) return "Saving...";
    if (!hasReviewers) return "No Reviewers";
    if (arePendingComments && !proposal?.isModifiedByBuilder(proposalBuilder)) {
      return "Share Comments";
    }
    if (proposal?.isModifiedByBuilder(proposalBuilder)) {
      return "Share Changes";
    }
    return "Nothing To Share";
  }

  function handleCommentsClicked(field: ProposalField, name?: string) {
    if (proposal?.id && !keepCommentsPanelClosed) {
      handleOpenCommentsPanel(field, name);
    } else {
      setCurrentCommentField(field);
      setCommentsPanelTitle(name);
    }
  }

  const commentsToolTip = proposal?.id ? "Add comments" : "Save proposal to add comments";
  const manageReviewersDisabled = !proposalBuilder ||
    !commentThreads ||
    isSaving ||
    isSelectingClient ||
    isSelectingClient ||
    isManagingReviewers ||
    reviewerSelectorAnchor !== undefined ||
    !proposal?.id ||
    disableEditing ||
    isReviewing;
  const reviewers = (session.context?.viewingAsVendor ? proposalBuilder?.currentSpec.vendorReviewers
    : proposalBuilder?.currentSpec.clientReviewers) ?? [];
  const totalApprovals = reviewers.filter((reviewer) => reviewer.dateApproved).length;
  const totalReviewers = reviewers.length ?? 0;
  const areChangesToShare = proposal?.isModifiedByBuilder(proposalBuilder) || arePendingComments;

  return (
    <>
      {!proposalBuilder && <NoBuilderLoader />}
      {proposalBuilder && (
        <>
          <Header>
            <TabsContainer
              variant="scrollable"
              scrollButtons="auto"
              indicatorColor="primary"
              textColor="primary"
              value={activeTab}
              onChange={handleTabChange}
            >
              <DialogTab
                value={ProposalFieldCategory.Details}
                disabled={shouldDisableTabs()}
                label={
                  <ProposalIssuesBadge
                    issues={issues}
                    commentThreads={commentThreads ?? []}
                    category={ProposalFieldCategory.Details}
                  />
                }
              />
              <DialogTab
                value={ProposalFieldCategory.Team}
                disabled={shouldDisableTabs()}
                label={
                  <ProposalIssuesBadge
                    issues={issues}
                    commentThreads={commentThreads ?? []}
                    category={ProposalFieldCategory.Team}
                  />
                }
              />
              <DialogTab
                value={ProposalFieldCategory.FeeSchedule}
                disabled={shouldDisableTabs()}
                label={
                  <ProposalIssuesBadge
                    issues={issues}
                    commentThreads={commentThreads ?? []}
                    category={ProposalFieldCategory.FeeSchedule}
                  />
                }
              />
              <DialogTab
                value={ProposalFieldCategory.Conflicts}
                disabled={shouldDisableTabs()}
                label={
                  <ProposalIssuesBadge
                    issues={issues}
                    commentThreads={commentThreads ?? []}
                    category={ProposalFieldCategory.Conflicts}
                  />
                }
              />
              <DialogTab
                value={ProposalFieldCategory.Policies}
                disabled={shouldDisableTabs()}
                label={
                  <ProposalIssuesBadge
                    issues={issues}
                    commentThreads={commentThreads ?? []}
                    category={ProposalFieldCategory.Policies}
                  />
                }
              />
              <DialogTab
                value={ProposalFieldCategory.Discount}
                disabled={shouldDisableTabs()}
                label={
                  <ProposalIssuesBadge
                    issues={issues}
                    commentThreads={commentThreads ?? []}
                    category={ProposalFieldCategory.Discount}
                  />
                }
              />
            </TabsContainer>
          </Header>
          <Content>
            <TabContent>
              {proposalBuilder && (
                <>
                  <DetailsTab
                    commentThreads={commentThreads ?? []}
                    commentsToolTip={commentsToolTip}
                    issues={issues}
                    activeTab={activeTab}
                    activeReviewField={activeReviewField}
                    proposalBuilder={proposalBuilder}
                    disableEditing={Boolean(disableEditing || (proposal && !proposal?.userCanEdit(session.user)))}
                    disableValidate={proposal?.isArchived}
                    onSelectingClientChange={(isSelecting: boolean) =>
                      setIsSelectingClient(isSelecting)
                    }
                    onProposalBuilderUpdated={handleProposalBuilderUpdated}
                    onCommentsClicked={handleCommentsClicked}
                  />
                  <TeamTab
                    entityId={session.currentEntity.entityId}
                    proposalBuilder={proposalBuilder}
                    issues={issues}
                    disableEditing={Boolean(disableEditing || (proposal && !proposal?.userCanEdit(session.user)))}
                    activeTab={activeTab}
                    activeReviewField={activeReviewField}
                    commentThreads={commentThreads ?? []}
                    commentsToolTip={commentsToolTip}
                    onProposalBuilderUpdated={handleProposalBuilderUpdated}
                    onCommentsClicked={handleCommentsClicked}
                  />
                  <FeeScheduleTab
                    entityId={session.currentEntity.entityId}
                    proposalBuilder={proposalBuilder}
                    issues={issues}
                    commentThreads={commentThreads}
                    commentsToolTip={commentsToolTip}
                    disableEditing={Boolean(disableEditing || (proposal && !proposal?.userCanEdit(session.user)))}
                    activeTab={activeTab}
                    activeReviewField={activeReviewField}
                    onProposalBuilderUpdated={handleProposalBuilderUpdated}
                    onCommentsClicked={handleCommentsClicked}
                  />
                  <ConflictsTab
                    proposalBuilder={proposalBuilder}
                    issues={issues}
                    commentThreads={commentThreads}
                    commentsToolTip={commentsToolTip}
                    disableEditing={Boolean(disableEditing || (proposal && !proposal?.userCanEdit(session.user)))}
                    activeTab={activeTab}
                    activeReviewField={activeReviewField}
                    onProposalBuilderUpdated={handleProposalBuilderUpdated}
                    onCommentsClicked={handleCommentsClicked}
                  />
                  <PoliciesTab
                    proposalBuilder={proposalBuilder}
                    issues={issues}
                    commentThreads={commentThreads}
                    disableEditing={Boolean(disableEditing || (proposal && !proposal?.userCanEdit(session.user)))}
                    activeTab={activeTab}
                    activeReviewField={activeReviewField}
                    commentsToolTip={commentsToolTip}
                    onProposalBuilderUpdated={handleProposalBuilderUpdated}
                    onCommentsClicked={handleCommentsClicked}
                  />
                  <DiscountTab
                    proposalBuilder={proposalBuilder}
                    issues={issues}
                    disableEditing={Boolean(disableEditing || (proposal && !proposal?.userCanEdit(session.user)))}
                    activeTab={activeTab}
                    activeReviewField={activeReviewField}
                    commentThreads={commentThreads ?? []}
                    commentsToolTip={commentsToolTip}
                    onProposalBuilderUpdated={handleProposalBuilderUpdated}
                    onCommentsClicked={handleCommentsClicked}
                  />
                </>
              )}
            </TabContent>
          </Content>
          <ActionsContainer>
            <SecondaryActions>
              <IssueTraverser
                issueFields={
                  _.uniqBy(
                    issues?.entries.map((issue) => issue.field) ?? [],
                    (field) => field.toJSON()
                  )
                }
                unreadCommentFields={
                  _.uniqBy(
                    commentThreads?.filter((thread) => thread.numUnread)
                      .map((thread) => thread.field) ?? [],
                    (field) => field.toJSON()
                  )
                }
                currentField={activeReviewField}
                prevLabel="Prev Issue"
                nextLabel="Next Issue"
                onTraverseToNewField={setActiveReviewField}
              />
            </SecondaryActions>
            <ProposalActions>
              {!disableEditing && (
                <>
                  <Fieldset>
                    {!isReviewing && (
                      <>
                        <Tooltip
                          title={!proposal?.id ? "Save proposal to add reviewers." : ""}
                        >
                          <ProposalActionSpan>
                            <ProposalActionButton
                              variant="contained"
                              color="primary"
                              startIcon={
                                <GroupsIcon />
                              }
                              endIcon={
                                <ReviewerBadge
                                  invisible={manageReviewersDisabled || !reviewers.length}
                                  badgeContent={
                                    <Tooltip
                                      title={totalApprovals === totalReviewers ? "All reviewers approved"
                                        : "Awaiting reviewer responses"}>
                                      <span>
                                        <sup>{totalApprovals}</sup>&frasl;<sub>{totalReviewers}</sub>
                                      </span>
                                    </Tooltip>
                                  }
                                  color={totalReviewers === totalApprovals ? "success" : "warning"}
                                />
                              }
                              loading={false}
                              disabled={manageReviewersDisabled}
                              onClick={handleManageReviewersClicked}
                            >
                              Reviewers
                            </ProposalActionButton>
                          </ProposalActionSpan>
                        </Tooltip>
                        <ReviewerSelector
                          proposal={proposal ?? undefined}
                          proposalBuilder={proposalBuilder}
                          popoverAnchor={reviewerSelectorAnchor}
                          onPopoverClose={() => {
                            setReviewerSelectorAnchor(undefined);
                            setIsManagingReviewers(false);
                          }}
                          onReviewersChanged={(updatedReviewers) => {
                            let updatedProposalBuilder;
                            if (session.context?.viewingAsVendor) {
                              updatedProposalBuilder = proposalBuilder?.setVendorReviewers(updatedReviewers);
                            } else {
                              updatedProposalBuilder = proposalBuilder?.setClientReviewers(updatedReviewers);
                            }
                            void handleProposalBuilderUpdated(updatedProposalBuilder);
                          }}
                          onSend={() => handleSaveAndShareChanges(true)}
                        />
                      </>
                    )}
                    {isReviewing && (
                      <Tooltip title={
                        hasUserApprovedReview()
                          ? "Proposal marked as reviewed"
                          : "Mark proposal as reviewed"
                      }>
                        <span>
                          <ProposalActionButton
                            variant="contained"
                            color="primary"
                            startIcon={<GradingIcon />}
                            loading={isSaving}
                            disabled={
                              !proposalBuilder ||
                              !commentThreads ||
                              isSaving ||
                              isApprovingReview ||
                              isSelectingClient ||
                              isManagingReviewers ||
                              hasUserApprovedReview()
                            }
                            onClick={handleMarkReviewedClicked}
                          >
                            {hasUserApprovedReview() ? "Reviewed" : "Mark Reviewed"}
                          </ProposalActionButton>
                        </span>
                      </Tooltip>
                    )}
                    <Tooltip title={
                      areChangesToShare
                        ? "Share changes and/or comments for review"
                        : "No reviewers or no unshared changes/comments"}>
                      <span>
                        <ProposalActionButton
                          variant="contained"
                          color="primary"
                          startIcon={<IosShareIcon />}
                          loading={isSaving}
                          disabled={
                            !areChangesToShare ||
                            isSaving ||
                            isApprovingReview ||
                            isSelectingClient ||
                            isManagingReviewers ||
                            !hasReviewers
                          }
                          onClick={() => handleSaveAndShareChanges(true)}
                        >
                          {getShareButtonLabel()}
                        </ProposalActionButton>
                      </span>
                    </Tooltip>
                  </Fieldset>
                  {!disableEditing && !isReviewing && (
                    <Tooltip
                      title={
                        !issues?.canSubmit
                          ? "Fill out required fields to submit."
                          : "Submit proposal to other party"
                      }
                    >
                      <ProposalActionSpan>
                        <ProposalActionButton
                          variant="contained"
                          color="success"
                          startIcon={<SendIcon />}
                          loading={isSubmitting}
                          disabled={
                            !proposalBuilder ||
                            isSaving ||
                            isSelectingClient ||
                            isManagingReviewers ||
                            !issues?.canSubmit ||
                            disableEditing ||
                            isReviewing
                          }
                          onClick={handleSubmitClicked}
                        >
                          Submit
                        </ProposalActionButton>
                      </ProposalActionSpan>
                    </Tooltip>
                  )}
                  {proposal?.creator?.userId.isEqualTo(session.user?.id) && (
                    <ProposalActionButton
                      variant="contained"
                      color="error"
                      startIcon={<DeleteIcon />}
                      loading={false}
                      disabled={
                        !proposalBuilder ||
                        !commentThreads ||
                        isSubmitting ||
                        isSaving ||
                        isManagingReviewers ||
                        reviewerSelectorAnchor !== undefined ||
                        !proposal?.id ||
                        isReviewing
                      }
                      onClick={handleDeleteClicked}
                    >
                      Delete Proposal
                    </ProposalActionButton>
                  )}
                </>
              )}
            </ProposalActions>
            <CommentButtons>
              <Tooltip
                title={!proposal?.id ? "Save proposal to enable commenting" : ""}
              >
                <ButtonContainer>
                  <HighlightCommentButton
                    active={activeReviewField?.isEqualTo(ProposalField.General)}
                    size="medium"
                    color="primary"
                    disabled={
                      !proposal?.id || !proposalBuilder || !commentThreads
                    }
                    onClick={() =>
                      handleOpenCommentsPanel(ProposalField.General)
                    }
                  >
                    <Badge
                      variant={generalFieldUnreadCount ? "standard" : "dot"}
                      color="secondary"
                      overlap="circular"
                      badgeContent={generalFieldUnreadCount > 0 ? generalFieldUnreadCount : undefined}
                      invisible={
                        !commentThreads?.some((thread) =>
                          thread.field.isEqualTo(ProposalField.General)
                        )
                      }
                    >
                      <CommentIcon fontSize="medium" />
                    </Badge>
                  </HighlightCommentButton>
                  <CommentButtonLabel
                    variant="button"
                    color={proposal?.id ? "primary" : "darkgray"}
                  >
                    Comments
                  </CommentButtonLabel>
                </ButtonContainer>
              </Tooltip>
            </CommentButtons>
            <Portal>
              <CommentsDrawer
                proposal={proposal}
                commentThreads={commentThreads}
                currentCommentField={currentCommentField}
                activeReviewField={activeReviewField}
                isOpen={isCommentsPanelOpen}
                isSaving={isSaving}
                title={commentsPanelTitle}
                onOpen={() => {
                  handleOpenCommentsPanel();
                  setKeepCommentsPanelClosed(false);
                }}
                onClose={handleCloseCommentsPanel}
              />
            </Portal>
          </ActionsContainer>
        </>
      )}
    </>
  )
}
